import { apiCweb } from './api-cweb';
import {
  DbAttachment,
  DbFileVersion,
  DbShare,
  DbShareKey,
  DbShareListItem,
  DbShareTender,
  DbShareTenderFolder,
  DbTenderFolder,
  TenderCoreRegVM,
  TenderRegistrationVM,
  TenderSubFolderCoreVM,
} from '~src/types/db';
import { appLog } from '~src/utils/log';
import BPromise from 'bluebird';
import { sortBy } from 'lodash';
import { getAttachment } from '~src/utils/apiUtils';
import { QueryKey } from 'react-query';
import { DB_STATE } from '~src/types/enums';
import { message } from 'antd';

export interface CreateShareParams {
  data: Pick<
    DbShare,
    'project_id' | 'is_protected' | 'description' | 'expires_at'
  >;
}
export interface SharedTenderParams {
  share_id: string;
  share_key?: string;
}
export const createShare = async ({ data }: CreateShareParams) => {
  appLog('createShare ->', { data });
  const res = await apiCweb.post<DbShare>(`/shares`, data);
  return res.data;
};

export interface AddFilesToShareParams {
  shareId: DbShare['id'];
  fileIds: DbFileVersion['id'][];
}
export const addFilesToShare = async ({
  shareId,
  fileIds,
}: AddFilesToShareParams) => {
  appLog('addFilesToShare ->', { shareId, fileIds });
  const res = await apiCweb.put<DbFileVersion[]>(
    `/shares/${shareId}/files`,
    fileIds
  );
  return res.data;
};

export interface CreateShareKeyParams {
  data: Pick<DbShareKey, 'share_id' | 'email' | 'title' | 'state'>;
}
/** Use this to trigger send to recipient emails */
export const createShareKey = async ({ data }: CreateShareKeyParams) => {
  appLog('createShareKey ->', { data });
  const res = await apiCweb.post<DbShareKey[]>(
    `/shares/${data.share_id}/share_keys`,
    data
  );
  return res.data;
};

export const getSharesList = async () => {
  appLog('loadSharesList');
  const res = await apiCweb.get('/shares');

  appLog('loadSharesList #2', res.data);
  return res.data;
};

export interface LoadShareParams {
  id: DbShare['id'];
  key?: DbShareKey['id'];
}
/** GET DbShare object */
export const loadShare = async (_, { id, key }: LoadShareParams) => {
  appLog('loadShare ->', { id, key });
  const res = await apiCweb.get<DbShare>(`/shares/${id}`, {
    params: {
      key: key,
    },
  });
  return res.data;
};

export interface LoadSharedFilesParams {
  shareId: DbShare['id'];
  key?: DbShareKey['id'];
  before?: number;
  withLink?: boolean;
}
/** GET `DbFileVersion` list for matching Share */
export const loadSharedFiles = async (
  _,
  { key, shareId, before, withLink }: LoadSharedFilesParams
) => {
  appLog('loadSharedFiles ->', { shareId, before, key });

  const res = await apiCweb.get<DbFileVersion[]>(`/shares/${shareId}/files`, {
    params: { before, share_key: key },
  });

  if (withLink) {
    await BPromise.map(res.data, async (file) => {
      file.location = await loadPublicShareLink({
        shareId,
        fileId: file.id,
        key,
      });
    });
  }

  return res.data;
};

export interface LoadPublicSharesLink {
  fileId: string;
  key?: DbShareKey['id'];
  shareId: DbShare['id'];
}

export const loadPublicShareLink = async ({
  fileId,
  shareId,
  key,
}: LoadPublicSharesLink) => {
  const res = await apiCweb.get(`/shares/${shareId}/files/${fileId}`, {
    params: {
      key: key,
    },
  });

  return res.data.location;
};

export const downloadPublicShareFileTwo = async ({
  fileId,
  shareId,
  key,
}: LoadPublicSharesLink) => {
  const res = await apiCweb.get(`/shares/${shareId}/files/${fileId}`, {
    params: {
      key: key,
    },
  });

  return res.data.location;
};

export const loadTenderShareCoreRegistration = async (
  _,
  { id, key }: LoadShareParams
): Promise<TenderCoreRegVM> => {
  appLog('loadTenderShareCoreRegistration ->', { id, key });
  const res = await apiCweb.get<TenderCoreRegVM>(
    `/tender_shares/${id}/core_registration`,
    {
      headers: { 'Share-Key': key },
    }
  );
  return res.data;
};

export const loadTenderShareCoreFolders = async ({
  id,
  key,
}: LoadShareParams) => {
  appLog('loadTenderShareCoreFolders ->', { id, key });
  const res = await apiCweb.get<DbShareTender>(
    `/tender_shares/${id}/core_folders`,
    {
      headers: { 'Share-Key': key },
    }
  );
  return res.data;
};

export const loadTenderShareKeyStatus = async ({
  id,
  key,
}: LoadShareParams) => {
  appLog('loadTenderShareKeyStatus ->', { id, key });
  const res = await apiCweb.get<DbShareTender>(`/tender_shares/${id}`, {
    params: {
      share_key: key,
    },
  });
  return res.data;
};

export const loadTendersFoldersShare = async ({ id, key }: LoadShareParams) => {
  appLog('loadTendersFoldersShare ->', { id, key });
  const res = await apiCweb.get<DbShareTenderFolder[]>(
    `/tender_shares/${id}/core_folders`,
    {
      headers: {
        'Share-Key': key,
      },
    }
  );
  return res.data;
};

export type LoadTendersSubFoldersShareParams = LoadShareParams & {
  rootFolderId?: string;
};

export const loadTendersSubFoldersShare = async ({
  id,
  key,
  rootFolderId,
}: LoadTendersSubFoldersShareParams) => {
  appLog('loadTendersSubFoldersShare ->', { id, key });
  const res = await apiCweb.get<TenderSubFolderCoreVM>(
    `/tender_shares/${id}/core_folders/${rootFolderId}`,
    {
      headers: {
        'Share-Key': key,
      },
    }
  );
  return res.data;
};

export const loadTendersAttachmentsShare = async ({ id, key }) => {
  appLog('loadTendersAttachmentsShare ->', { id, key });
  try {
    const res = await apiCweb.get(`/tender_shares/${id}/attachments`, {
      headers: {
        'Share-Key': key,
      },
    });

    // console.log(`loadTendersAttachmentsShare res.data -> `, res.data);
    return res.data;
  } catch (err) {
    const errResponse =
      err.response?.data?.message || err.response?.data?.error || err.message;
    appLog('share attachment err: ', errResponse);

    throw errResponse;
  }
};

export const loadSharedTenderFolders = async (
  key,
  { share_id, share_key }: SharedTenderParams
) => {
  appLog('loadSharedTenderFolders ->', { share_id, share_key });
  const res = await apiCweb.get<DbTenderFolder[]>(
    `/tender_shares/${share_id}/tender_folders`,
    {
      headers: {
        'Share-Key': share_key,
      },
    }
  );

  const sorted = sortBy(res.data, [
    (folder) => (folder.name || '').toUpperCase(),
  ]);
  return sorted;
};

export interface LoadSharedSubfolderFoldersParams extends SharedTenderParams {
  root_folder_id?: number;
}

export const loadSharedTenderSubfolders = async (
  queryKey: QueryKey,
  { share_id, share_key, root_folder_id }: LoadSharedSubfolderFoldersParams
) => {
  appLog('loadSharedTenderFoldersFolders ->', {
    share_id,
    share_key,
    root_folder_id,
  });

  const res = await apiCweb.get<DbTenderFolder[]>(
    `/tender_shares/${share_id}/tender_folders/${root_folder_id}/folders`,
    {
      headers: {
        'Share-Key': share_key,
      },
    }
  );

  const sorted = sortBy(res.data, [
    (folder) => (folder.name || '').toUpperCase(),
  ]);
  return sorted;
};

export interface LoadSharedFoldersFoldersIdParams
  extends LoadSharedSubfolderFoldersParams {
  folder_id?: string;
}

export const loadTenderFoldersFoldersId = async (
  key,
  {
    share_id,
    share_key,
    root_folder_id,
    folder_id,
  }: LoadSharedFoldersFoldersIdParams
) => {
  appLog('loadTenderFoldersFoldersId ->', {
    share_id,
    share_key,
    root_folder_id,
    folder_id,
  });

  const res = await apiCweb.get<DbTenderFolder>(
    `/tender_shares/${share_id}/tender_folders/${root_folder_id}/folders/${folder_id}`,
    {
      headers: {
        'Share-Key': share_key,
      },
    }
  );

  return res.data;
};

// export const loadTenderFoldersFolders = async (
//   key,
//   { projectId, favourites, root_folder_id }: LoadFoldersParams
// ) => {
//   appLog('loadTenderFoldersFolders ->', { projectId, favourites });
//   const res = await apiCweb.get<DbTenderFolder[]>(
//     `/tender_shares/${root_folder_id}/tender_folders`,
//     {
//       params: {},
//     }
//   );

//   return res.data;
// };

export const loadSharedTendersTenderAttachments = async (
  key: QueryKey,
  { share_id, share_key }: SharedTenderParams
) => {
  appLog('loadTenderAttachments ->', { share_id, share_key });

  const res = await apiCweb.get<DbAttachment[]>(
    `/tender_shares/${share_id}/attachments`,
    {
      headers: { 'Share-Key': share_key },
    }
  );

  const sorted = sortBy(res.data, [
    (folder) => (folder.name || '').toUpperCase(),
  ]);
  return sorted;
};

type LoadSharedTenderAttachmentParams = {
  share_id?: string;
  share_key?: string;
  file_guid?: string;
};

export const loadSharedTenderAttachment = async ({
  share_id,
  share_key,
  file_guid,
}: LoadSharedTenderAttachmentParams) => {
  appLog('loadSharedTenderAttachment -> ', {
    share_id,
    share_key,
    file_guid,
  });

  return getAttachment(
    {
      url: `/tender_shares/${share_id}/attachments/${file_guid}/location`,
    },
    {
      headers: { 'Share-Key': share_key },
    }
  );
};

type LoadSharedTenderVersionedFilesParams = LoadSharedTenderAttachmentParams & {
  root_folder_id?: string;
  folder_id?: string;
  versioned_file_id?: string;
};

export const loadSharedTenderVersionedFile = async ({
  share_id,
  share_key,
  root_folder_id,
  versioned_file_id,
  folder_id,
  file_guid,
}: LoadSharedTenderVersionedFilesParams) => {
  appLog('loadSharedTenderVersionedFile -> ', {
    share_id,
    share_key,
    root_folder_id,
    versioned_file_id,
    folder_id,
    file_guid,
  });

  return getAttachment(
    {
      url: `/tender_shares/${share_id}/tender_folders/${root_folder_id}/folders/${folder_id}/versioned_files/${versioned_file_id}/files/${file_guid}/location`,
    },
    {
      headers: { 'Share-Key': share_key },
    }
  );
};

// interface LoadSharedFoldersFilesParams
//   extends LoadSharedFoldersFoldersIdParams {}

export const loadSharedTenderFolderFiles = async (
  key: string,
  {
    share_id,
    share_key,
    root_folder_id,
    folder_id,
  }: LoadSharedFoldersFoldersIdParams
) => {
  appLog('loadFiles ->', {
    share_id,
    share_key,
    root_folder_id,
    folder_id,
  });
  const res = await apiCweb.get<DbAttachment[]>(
    `/tender_shares/${share_id}/tender_folders/${root_folder_id}/folders/${folder_id}/versioned_files`,
    // `root_id` is actually `folder_id`, and behaves differently then `root_id` on `GET /folders`
    {
      // params: {
      //   root_id: folderId,
      //   take: limit,
      //   favorites: favourites,
      //   after: afterDate,
      //   state: state?.toString(),
      //   show_deleted: showDeleted,
      //   before: before,
      //   search_name,
      //   search_description,
      //   search_revision,
      //   any_tags_of,
      //   all_tags_of,
      // },

      headers: {
        'Share-Key': share_key,
      },
    }
  );
  // if (!withVersions) return res.data;

  // if (oldFilesImplementation) {
  //   // attach file versions
  //   await BPromise.map(
  //     res.data,
  //     async (file) => {
  //       file.versions = await loadFileVersions('versions', {
  //         projectId,
  //         folderId,
  //         versionedFileId: file.id,
  //         before: before,
  //       });
  //     },
  //     { concurrency: 20 }
  //   );
  //   if (localSortBy) return sortBy(res.data, localSortBy);
  //   return res.data;
  // }

  // const fileVersions = await loadFileVersions('versions', {
  //   projectId,
  //   folderId,
  //   before,
  // });

  // const files = res.data.map((item) => ({
  //   ...item,
  //   versions: _.uniqBy(
  //     fileVersions.filter((file) => file.versioned_file_id === item.id),
  //     (e) => e.id
  //   ),
  // }));

  // if (localSortBy) return sortBy(res.data, localSortBy);
  // if (localSortBy) return sortBy(files, localSortBy);
  return res.data;
};

type ShareParams = {
  shareId: string;
  shareKey: string;
};

export type UpdateSharedTenderState = ShareParams & {
  action: 'reject';
};

export const updateSharedTenderState = async ({
  shareId,
  shareKey,
  action,
}: UpdateSharedTenderState) => {
  appLog('updateSharedTenderState -> ', {
    shareId,
    shareKey,
    action,
  });

  return apiCweb.put(
    `/tender_shares/${shareId}`,
    {},
    {
      params: { action },
      headers: { 'Share-Key': shareKey },
    }
  );
};

export const claimSharedTender = async ({ shareId, shareKey }: ShareParams) => {
  appLog('claimSharedTender -> ', {
    shareId,
    shareKey,
  });

  return apiCweb.post(
    `/shares/${shareId}/user_claim`,
    {},
    {
      headers: { 'Share-Key': shareKey },
    }
  );
};

export interface SharedTendersListParams {
  project_id?: string;
  extended_project_data?: boolean;
}

export const loadSharedTenders = async (_, params: SharedTendersListParams) => {
  const res = await apiCweb.get<DbShareListItem[]>(`/my_shares`, { params });
  return res.data;
};

export interface LoadMyTenderSharesParams {
  skip?: number;
  take?: number;
}

/**
 * GET /v1/my_tender_shares
 * Primary-/Secondary Share Keys upgrade (2.1 API Upgrades). Not wired into
 * any UI yet - added for completeness alongside the other confirmed field
 * updates on this endpoint family (/shares/{share_id}, /my_shares).
 */
export const loadMyTenderShares = async (
  _,
  params: LoadMyTenderSharesParams
) => {
  appLog('loadMyTenderShares ->', { params });
  const res = await apiCweb.get<DbShareListItem[]>('/my_tender_shares', {
    params,
  });
  return res.data;
};

export interface TenderSharesListParams {
  projectId?: number;
  tenderId?: string;
  states?: DB_STATE[];
  email?: string;
}

export const loadSharedAdminTenders = async (
  params: TenderSharesListParams
) => {
  appLog('loadSharedAdminTenders ->', { params });
  const res = await apiCweb.get<DbShare[]>(
    `/projects/${params.projectId}/tenders/${params.tenderId}/share_keys`,
    {
      params: {
        states: params.states?.join(','),
        email: params.email,
      },
    }
  );
  return res.data;
};

export interface LoadAdminSecondaryShareKeysParams {
  // Optional, matching TenderSharesListParams above - lets callers pass
  // this straight from possibly-undefined component props without
  // `?? ''`/`?? 0` fallback juggling; the query itself stays disabled via
  // `enabled` until all three are actually present.
  projectId?: number;
  tenderId?: string;
  shareKey: string; // primary share_key_id
  states?: DB_STATE[];
}

// Primary-/Secondary Share Keys (Tender Admin Update): admin-scoped
// equivalent of secondaryVendors.ts's loadSecondaryShareKeys - uses normal
// admin auth (projectId/tenderId, no Share-Key header) instead of the
// vendor's Share-Key-header-authenticated GET /tender_shares/{id}/... path.
export const loadAdminSecondaryShareKeys = async (
  params: LoadAdminSecondaryShareKeysParams
) => {
  appLog('loadAdminSecondaryShareKeys ->', { params });
  const res = await apiCweb.get<DbShare[]>(
    `/projects/${params.projectId}/tenders/${params.tenderId}/share_keys/${params.shareKey}/secondary_share_keys`,
    {
      params: {
        states: params.states?.join(','),
      },
    }
  );
  return res.data;
};

export interface LoadQualifiedShareKeysParams {
  projectId: number;
  tenderId: number;
  has_evaluated_accepted_offer?: boolean;
}

export const loadQualifiedShareKeys = async (
  params: LoadQualifiedShareKeysParams
) => {
  appLog('loadQualifiedShareKeys ->', { params });
  const res = await apiCweb.get<DbShare[]>(
    `/projects/${params.projectId}/tenders/${params.tenderId}/share_keys`,
    {
      params: {
        has_evaluated_accepted_offer: params.has_evaluated_accepted_offer,
      },
    }
  );
  return res.data;
};

export const loadSharedTenderDetails = async ({
  id,
  shareKey,
}: {
  id: string;
  shareKey?: string;
}): Promise<TenderRegistrationVM> => {
  appLog('loadSharedTenderDetails ->', {});

  const res = await apiCweb.get<TenderRegistrationVM>(
    `tender_shares/${id}/registration`,
    {
      headers: { 'Share-Key': shareKey },
    }
  );

  return res.data;
};

export const deleteTenderShare = async ({
  projectId,
  tenderId,
  shareKey,
}: {
  projectId: number;
  tenderId: number;
  shareKey: string;
}) => {
  appLog('deleteTenderShare ->', {});

  const res = await apiCweb.delete<DbShare>(
    `projects/${projectId}/tenders/${tenderId}/share_keys/${shareKey}`
  );

  return res.data;
};

export interface EditShareParams {
  projectId: number;
  tenderId: number;
  shareKey: string;
  data: {
    email: string;
    title: string;
  };
}

export const editShare = async ({
  projectId,
  tenderId,
  shareKey,
  data,
}: EditShareParams) => {
  appLog('editShare ->', { projectId, tenderId, shareKey, data });
  const res = await apiCweb.put<DbFileVersion[]>(
    `projects/${projectId}/tenders/${tenderId}/share_keys/${shareKey}`,
    data,
    {
      params: {
        action: 'update',
      },
    }
  );
  return res.data;
};

export interface UpdateShareParams {
  projectId: number | undefined;
  tenderId: number | string;
  shareKey: string;
  action: 'reactivate' | 'remove';
}

export const updateShare = async ({
  projectId,
  tenderId,
  shareKey,
  action,
}: UpdateShareParams) => {
  appLog('updateShare ->', { projectId, tenderId, shareKey, action });
  const res = await apiCweb.put<DbFileVersion[]>(
    `projects/${projectId}/tenders/${tenderId}/share_keys/${shareKey}`,
    {},
    {
      params: {
        action,
      },
    }
  );
  return res.data;
};

export interface ResendInviteShareParams {
  projectId: number;
  tenderId: number;
  shareKey: string;
}

// automatic w/out comment
export const resendInviteShare = async ({
  projectId,
  tenderId,
  shareKey,
}: ResendInviteShareParams) => {
  appLog('resendInviteShare ->', { projectId, tenderId, shareKey });
  try {
    const res = await apiCweb.put<DbFileVersion[]>(
      `projects/${projectId}/tenders/${tenderId}/share_keys?action=send_automated_invitation_email`,
      [{ share_key: shareKey }]
    );
    // console.log(`resendInviteShare ->`, res);
    return res.data;
  } catch (e) {
    appLog(e);
    throw e;
  }
};

export interface ResendEditInviteShareParams {
  projectId: number;
  tenderId: number;
  shareKey: string;
  description: string;
}

// automatic w/out comment
export const resendEditInviteShare = async ({
  projectId,
  tenderId,
  shareKey,
  description,
}: ResendEditInviteShareParams) => {
  appLog('resendEditInviteShare ->', {
    projectId,
    tenderId,
    shareKey,
    description,
  });
  try {
    const res = await apiCweb.put<DbFileVersion[]>(
      `projects/${projectId}/tenders/${tenderId}/share_keys?action=send_manual_invitation_email`,
      {
        description,
        share_keys: [shareKey],
      }
    );
    // console.log(`resendEditInviteShare ->`, res);
    return res.data;
  } catch (e) {
    appLog(e);
    throw e;
  }
};

export interface getShareLogsParams {
  projectId: number;
  tenderId: number;
  shareKey: string;
}

// Get Share Logs
export const getShareLogs = async ({
  projectId,
  tenderId,
  shareKey,
}: getShareLogsParams) => {
  appLog('getShareLogs ->', {
    projectId,
    tenderId,
    shareKey,
  });
  try {
    const res = await apiCweb.get(
      `projects/${projectId}/tenders/${tenderId}/share_keys/${shareKey}/logs`
    );
    // console.log(`getShareLogs answer ->`, res.data);
    return res.data;
  } catch (e) {
    appLog(e);
  }
};

export interface postShareKeysProps {
  projectId: number;
  tenderId: number;
  body: {
    email: string;
    title: string;
  }[];
}

// Add share keys to the tender
export const postShareKeys = async ({
  projectId,
  tenderId,
  body,
}: postShareKeysProps) => {
  appLog('postShareKeys ->', {
    projectId,
    tenderId,
    body,
  });
  try {
    const res = await apiCweb.post(
      `projects/${projectId}/tenders/${tenderId}/share_keys`,
      body
    );
    // console.log(`postShareKeys answer ->`, res.data);
    return res.data;
  } catch (e) {
    appLog('postShareKeys error ->', e);
    throw e;
  }
};

// Add a or more share key to the current invitation queue (trigger email sending)
export const patchInviteMessages = async ({
  projectId,
  tenderId,
  body,
}: {
  projectId: number | string;
  tenderId: number | string;
  body: Array<{
    share_key: string;
    email: string;
    title: string;
  }>;
}) => {
  appLog('patchInviteMessages ->', {
    projectId,
    tenderId,
    body,
  });

  const res = await apiCweb.patch(
    `projects/${projectId}/tenders/${tenderId}/share_keys/invite_messages`,
    body
  );

  return res.data;
};

export interface createOfferParams {
  share_id: string;
  share_key: string;
  contract_id: number;
}

export const createOffer = async ({
  share_id,
  share_key,
  contract_id,
}: createOfferParams) => {
  appLog('createOffer ->', { share_id, share_key, contract_id });
  try {
    const res = await apiCweb.post(
      `tender_shares/${share_id}/offers?contract_id=${contract_id}`,
      {},
      {
        headers: {
          'Share-Key': share_key,
        },
      }
    );

    // console.log(`createOffer #2 ->`, res.data);
    return res.data;
  } catch (err) {
    appLog(err);
  }
};

export const getVoucherResponses = async ({
  share_id,
  share_key,
  offer_id,
}: {
  share_id: string;
  share_key: string;
  offer_id: number;
}) => {
  const response = await apiCweb.get(
    `tender_shares/${share_id}/offers/${offer_id}/voucher_responses`,
    {
      headers: {
        'Share-Key': share_key,
      },
    }
  );
  // console.log('getVoucherResponses ->', response.data);
  return response.data;
};

export const submitVoucherResponse = async ({
  share_id,
  share_key,
  offer_id,
  voucher_id,
  data,
}: {
  share_id: string;
  share_key: string;
  offer_id: number;
  voucher_id: number;
  data: {
    sum?: number | null;
    is_approved?: boolean | null;
  };
}) => {
  // console.log('submitVoucherResponse ->', {
  //   share_id,
  //   share_key,
  //   offer_id,
  //   voucher_id,
  //   data,
  // });
  const response = await apiCweb.put(
    `/tender_shares/${share_id}/offers/${offer_id}/vouchers/${voucher_id}/responses`,
    data,
    {
      headers: {
        'Share-Key': share_key,
      },
    }
  );

  appLog('submitVoucherResponse ->', response.data);
  return response.data;
};

export const actionOffer = async ({
  share_id,
  share_key,
  offer_id,
  action,
}: {
  share_id: string;
  share_key: string;
  offer_id: number;
  action: string;
}) => {
  appLog('finalizeOffer ->', {
    share_id,
    share_key,
    offer_id,
    action,
  });
  const response = await apiCweb.put(
    `tender_shares/${share_id}/offers/${offer_id}?action=${action}`,
    {},
    {
      headers: {
        'Share-Key': share_key,
      },
    }
  );

  appLog('actionOffer ->', response.data);
  return response.data;
};

export const getShareStatus = async ({
  shareId,
  shareKey,
}: {
  shareId: string;
  shareKey: string;
}) => {
  appLog('getShareStatus ->', { shareId, shareKey });

  const res = await apiCweb.get(`/shares/${shareId}`, {
    headers: {
      'Share-Key': shareKey,
    },
  });

  appLog('getShareStatus <-', res.data);
  return res.data as any;
};

// types
export interface ShareKeyValidationRequestDto {
  share_id: string;
  key: string;
}

export interface ShareKeyValidityStatus {
  is_key_active?: boolean;
  is_key_expired?: boolean;
  is_key_claimed?: boolean;
  is_key_rejected?: boolean;
}

export interface ShareKeyValidationResponseDto
  extends ShareKeyValidationRequestDto {
  is_valid: boolean;
  validity_status: ShareKeyValidityStatus;
}

// batch validate multiple share-keys
export const validateShareKeys = (
  payload: ShareKeyValidationRequestDto[]
): Promise<ShareKeyValidationResponseDto[]> =>
  apiCweb
    .post<ShareKeyValidationResponseDto[]>('/share_keys/validate', payload)
    .then((res) => res.data ?? []);

// keep single-key helper for existing code
export const validateShareKey = (params: {
  shareId?: string;
  shareKey: string;
}): Promise<ShareKeyValidationResponseDto | undefined> =>
  validateShareKeys([
    {
      share_id: params.shareId ?? '',
      key: params.shareKey,
    },
  ]).then((list) => list[0]);

// reject share key with optional message
export interface RejectShareKeyRequestDto {
  shareId: string;
  shareKey: string;
  message?: string;
}

export const rejectShareKey = ({
  shareId,
  shareKey,
  message,
}: RejectShareKeyRequestDto): Promise<void> =>
  apiCweb
    .put(`/shares/${shareId}?action=reject`, message ? { message } : {}, {
      headers: {
        'Share-Key': shareKey,
      },
    })
    .then(() => undefined);

export interface UpdateShareKeyProps {
  projectId: number;
  tenderId: number;
  shareKey: string;
  body: { email: string; title: string };
}

export const updateShareKey = async ({
  projectId,
  tenderId,
  shareKey,
  body,
}: UpdateShareKeyProps) => {
  appLog('updateShareKey ->', { projectId, tenderId, shareKey, body });
  const res = await apiCweb.put(
    `projects/${projectId}/tenders/${tenderId}/share_keys/${shareKey}?action=update`,
    body
  );
  return res.data;
};

// get shares invitation's message & title
export const getShareMessage = async ({ projectId, tenderId }) => {
  appLog('getShareMessage ->', { projectId, tenderId });

  try {
    const res = await apiCweb.get(
      `/projects/${projectId}/tenders/${tenderId}/latest_invite_message`
    );

    return res.data;
  } catch (error: any) {
    const status = error?.response?.status;

    if (status === 404) {
      appLog('getShareMessage 404 -> no invite message, returning empty', {
        projectId,
        tenderId,
      });

      return {
        title: '',
        description: '',
      };
    }

    appLog('getShareMessage error', error);
    throw error;
  }
};

// post shares invitation's message & title
export const postShareMessage = async ({
  projectId,
  tenderId,
  body,
}: {
  projectId: number | undefined;
  tenderId: number | undefined;
  body: {
    title: string;
    description: string;
  };
}) => {
  appLog('getShareMessage ->', { projectId, tenderId });
  const res = await apiCweb.post(
    `projects/${projectId}/tenders/${tenderId}/share_key_invite_messages`,
    body
  );
  return res.data;
};

// Vendor Side: Tender Folder's Download (tender_shares)
export const newTenderShareFolderDownload = async (
  key: string,
  {
    shareId,
    shareKey,
    rootFolderId,
    folderId,
  }: {
    shareId: string;
    shareKey: string;
    rootFolderId: number | string;
    folderId: number | string;
  }
) => {
  appLog('newTenderShareFolderDownload ->', {
    shareId,
    rootFolderId,
    folderId,
  });

  try {
    const res = await apiCweb.get(
      `/tender_shares/${shareId}/tender_folders/${rootFolderId}/folders/${folderId}/versioned_files/file_download`,
      {
        params: {
          recursive: true,
          skip: 0,
        },
        headers: {
          'Share-Key': shareKey,
        },
        timeout: 30000,
      }
    );

    return res.data;
  } catch (error) {
    console.error(`Failed to load files for folder: ${folderId}`, error);
    return [];
  }
};

export const getTenderShareFolderFilesURLs = async (
  shareId: string,
  shareKey: string,
  using_keys: string[],
  rootFolderId?: string | number
) => {
  try {
    const res = await apiCweb.post(
      `/tender_shares/${shareId}/tender_folders/batch_download_urls`,
      { using_keys },
      {
        headers: {
          'Share-Key': shareKey,
        },
        params:
          rootFolderId != null ? { root_folder_id: rootFolderId } : undefined,
        timeout: 30000,
      }
    );

    return res.data;
  } catch (error) {
    return [];
  }
};

export interface GetSharedTenderVersionedFilesDownloadListParams {
  shareId: string | number;
  shareKey: string;
  rootFolderId: string | number;
  folderId: string | number;
  recursive?: boolean;
  registration_ids?: string;
  serial?: string | number;
  skip?: number;
  take?: number;
}

export const getSharedTenderVersionedFilesDownloadList = async ({
  shareId,
  shareKey,
  rootFolderId,
  folderId,
  recursive = true,
  registration_ids,
  serial,
  skip,
  take,
}: GetSharedTenderVersionedFilesDownloadListParams) => {
  try {
    const res = await apiCweb.get(
      `/tender_shares/${shareId}/tender_folders/${rootFolderId}/folders/${folderId}/versioned_files/file_download`,
      {
        headers: {
          'Share-Key': shareKey,
        },
        params: {
          recursive,
          registration_ids,
          skip,
          serial,
          take,
        },
        timeout: 30000,
      }
    );

    return res.data;
  } catch (error) {
    return [];
  }
};

export interface LoadPublicTenderCoreRegistrationParams {
  tenderShareId: string;
}

export const loadPublicTenderCoreRegistration = async (
  _,
  { tenderShareId }: LoadPublicTenderCoreRegistrationParams
): Promise<TenderCoreRegVM> => {
  appLog('loadPublicTenderCoreRegistration ->', { tenderShareId });

  const res = await apiCweb.get<TenderCoreRegVM>(
    `/public_tenders/${tenderShareId}`
  );

  return res.data;
};

export type CreatePublicTenderSupplierPayload = {
  tenderShareId: string;
  supplier_name: string;
  contact_email: string;
};

export const createPublicTenderSupplier = ({
  tenderShareId,
  supplier_name,
  contact_email,
}: CreatePublicTenderSupplierPayload) =>
  apiCweb.post(`/public_tenders/${tenderShareId}/suppliers`, {
    supplier_name,
    contact_email,
  });

export const loadPublicTenderCoreFolders = async ({
  tenderShareId,
}: {
  tenderShareId: string;
}) => {
  const { data } = await apiCweb.get(
    `/public_tenders/${tenderShareId}/core_folders`
  );

  return data;
};

export const loadPublicTenderCoreFolder = async ({
  tenderShareId,
  rootFolderId,
}: {
  tenderShareId: string;
  rootFolderId: string;
}) => {
  const { data } = await apiCweb.get(
    `/public_tenders/${tenderShareId}/core_folders/${rootFolderId}`
  );

  return data;
};

export const loadPublicTenderCoreContracts = async ({
  tenderShareId,
}: {
  tenderShareId: string;
}) => {
  const { data } = await apiCweb.get(
    `/public_tenders/${tenderShareId}/core_contracts`
  );

  return data;
};

// Q&A Answer notification preview
export interface QAAnswerPreviewParams {
  projectId: number;
  tenderId: number;
  title: string;
  description: string;
}

export const getQAAnswerEmailPreview = async ({
  projectId,
  tenderId,
  title,
  description,
}: QAAnswerPreviewParams): Promise<string> => {
  const url = `/projects/${projectId}/tenders/${tenderId}/questions/answer_email_preview_as_html`;
  appLog('getQAAnswerEmailPreview ->', url, { title, description });

  const response = await apiCweb.post(
    url,
    { title, description },
    { headers: { Accept: 'text/html' } }
  );
  appLog('getQAAnswerEmailPreview #2 -> HTML received');
  return response.data;
};

// Invitation message template preview
export interface InviteMessagePreviewParams {
  projectId: number;
  tenderId: number;
  title: string;
  message: string;
}

export const getInviteMessagePreview = async ({
  projectId,
  tenderId,
  title,
  message,
}: InviteMessagePreviewParams): Promise<string> => {
  const url = `/projects/${projectId}/tenders/${tenderId}/invite_messages_template_preview_as_html`;
  appLog('getInviteMessagePreview ->', url, { title, description: message });

  const response = await apiCweb.post(
    url,
    { title, description: message },
    { headers: { Accept: 'text/html' } }
  );
  appLog('getInviteMessagePreview #2 -> HTML received');
  return response.data;
};
